<script setup lang="ts">
  import JSONEditor from 'jsoneditor'
  import { onMounted, onUnmounted, ref, watch } from 'vue'
  import { DataCleaningService } from '../services/DataCleaningService'
  import { Button, message } from 'ant-design-vue'
  import { CheckCircle2 } from 'lucide-vue-next'
  import { DataCleaningTypeGuardService } from '../services/DataCleaningTypeGuardService'
  import { RuleJsonEditorSchema } from '../services/DataCleaningJsonRuleConfigurationService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import { RuleSchema } from '../models/RuleSchema'
  import { AvailableColumn } from '../models/AvailableColumn'

  interface IJsonRuleConfigurationProps {
    ruleSchemas: Array<RuleSchema>
    availableColumns: Array<AvailableColumn>
    modelValue: {}
    placeHolder?: string
    configurationLevel: number
    editorSchema: RuleJsonEditorSchema
    allowTypeChange: boolean
  }

  interface IJsonRuleConfigurationEmits {
    (e: 'update:modelValue', value: {}): void
    (e: 'rule-type-changed', type: string, configuration: {}): void
  }

  const props = defineProps<IJsonRuleConfigurationProps>()
  const emit = defineEmits<IJsonRuleConfigurationEmits>()

  const modelValue = ref<{}>({ ...props.modelValue })

  watch(
    modelValue,
    (newVal) => {
      emit('update:modelValue', newVal)
    },
    { deep: true }
  )

  const newApplyableRuleType = ref<string | null>(null)

  const editorContainer = ref<HTMLElement | null>(null)
  let editor: any = null

  const initializeJSONEditor = () => {
    if (editorContainer.value) {
      editor = new JSONEditor(editorContainer.value, {
        mode: 'code',
        schema: props.editorSchema,
        onError: (err: Error) => console.error('Error:', err),
        onChange: () => {
          try {
            const newVal = editor.get()
            if (props.allowTypeChange === true) {
              onEditorChangeForRule(newVal)
            } else {
              onEditorChangeForField(newVal)
            }
          } catch (err: any) {
            const editorValue = editor.getText?.() ?? ''
            if (editorValue.trim() === '') {
              message.error('Editor content cannot be empty.', defaultMessageDurationSeconds)
            }
          }
        }
      })
      setJSONEditorModel(modelValue.value)
    }
  }

  const onEditorChangeForRule = (editorValue: any) => {
    newApplyableRuleType.value = null
    if (typeof editorValue === 'object') {
      if (editorValue.type != null) {
        const ruleType = DataCleaningService.tryGetRuleSchemaByType(
          editorValue.type,
          props.ruleSchemas
        )
        if (
          ruleType != null &&
          ruleType.type !== DataCleaningService.CUSTOM_RULE_TYPE &&
          (props.configurationLevel === 1 ||
            !DataCleaningTypeGuardService.supportsChildRules(ruleType))
        ) {
          newApplyableRuleType.value = ruleType.type
        }
      }
      modelValue.value = editorValue
    }
  }

  const onEditorChangeForField = (editorValue: any) => {
    modelValue.value = editorValue
  }

  const setJSONEditorModel = (value: {}) => {
    if (editor != null) {
      newApplyableRuleType.value = null
      editor.set(value)
      if (editor.getMode() === 'tree') editor.expandAll()
    }
  }

  const onApplyNewRuleType = () => {
    if (newApplyableRuleType != null) {
      emit('rule-type-changed', newApplyableRuleType.value as string, modelValue)
    }
  }

  onMounted(() => {
    initializeJSONEditor()
  })

  onUnmounted(() => {
    if (editor) {
      editor.destroy()
    }
  })
</script>

<template>
  <div class="flex flex-col">
    <div v-if="props.allowTypeChange === true" class="mb-3 flex justify-end">
      <a-tooltip>
        <template #title>
          {{ `Apply Rule type: ${newApplyableRuleType}` }}
        </template>
        <Button type="primary" :disabled="newApplyableRuleType == null" @click="onApplyNewRuleType">
          <span class="ant-button-flex-content">
            <CheckCircle2 class="h-4 w-4" />
            {{ 'Apply Rule Type' }}
          </span>
        </Button>
      </a-tooltip>
    </div>
    <div ref="editorContainer" style="min-height: 500px; height: 500px"></div>
  </div>
</template>
